/**
 * DigestDetailDialog — drill-down for a single Digest tile / row.
 *
 * Fetches GET /reports/digest/detail and renders the returned sections
 * (columns + rows + optional total). All cells arrive pre-formatted from the
 * backend, so this component only lays them out.
 */
import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { extractError } from '@/lib/errors';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Loader2, Building2, User, Hammer } from 'lucide-react';

// Packet-detail routes keyed by raw-material kind (Task 1 — clickable packet nos).
const PACKET_ROUTE = { diamond: 'diamonds', color_stone: 'color-stones', pearl: 'pearls' };

// Per-material progress-bar palette (Task 5 — RM Issued fulfilled %).
// Thin gradient bar (native app colours) + inline % text.
const BAR = {
  gold:    { from: 'var(--champagne)', to: 'var(--gold-accent)',       text: 'var(--gold-accent)' },
  diamond: { from: '#7FB0E6',          to: 'var(--diamond-icon)',      text: 'var(--diamond-icon)' },
  cs:      { from: '#E39BBB',          to: 'var(--color-stones-icon)', text: 'var(--color-stones-icon)' },
  pearl:   { from: '#F0C39A',          to: 'var(--pearls-icon)',       text: 'var(--pearls-icon)' },
};

// Material columns that can carry a fulfilled-% bar. When a section has any
// progress bars (RM Issued), a material that was *issued but has no
// requirement raised* still reserves the bar's height + shows a muted "no req."
// tag, so the column stays aligned and the missing bar reads as intentional.
const MATERIAL_KEYS = new Set(['gold', 'diamond', 'cs', 'pearl']);

export const DigestDetailDialog = ({ open, onOpenChange, params, label }) => {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!open || !params?.subject) return;
    setLoading(true);
    setDetail(null);
    (async () => {
      try {
        const { data } = await api.get('/reports/digest/detail', { params });
        if (!cancelled) setDetail(data);
      } catch (e) {
        if (!cancelled) {
          extractError(e, 'Failed to load detail');
          setDetail({ sections: [], _error: extractError(e, 'Failed to load detail') });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, params?.subject, params?.company_id, params?.start, params?.end]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-[96vw] max-h-[85vh] overflow-y-auto" data-testid="digest-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {label || detail?.label || 'Detail'}
          </DialogTitle>
        </DialogHeader>

        {detail?.company_name && (
          <div className="flex items-center gap-1.5 text-xs text-slate-500 -mt-1 mb-1">
            <Building2 size={12} /> {detail.company_name}
            {detail?.period?.label && <span className="text-slate-400">· {detail.period.label}</span>}
          </div>
        )}

        {loading ? (
          <div className="space-y-2 py-4" data-testid="digest-detail-loading">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-2/3" />
          </div>
        ) : detail?.blocked ? (
          <div className="py-8 text-center text-sm text-slate-500">This activity isn’t available for your role.</div>
        ) : detail?._error ? (
          <div className="py-8 text-center text-sm text-rose-600">{detail._error}</div>
        ) : (
          <div className="space-y-5">
            {(detail?.sections || []).map((sec, si) => (
              <Section key={si} section={sec} />
            ))}
            {(!detail?.sections || detail.sections.length === 0) && (
              <div className="py-8 text-center text-sm text-slate-500">No detail available.</div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

function Section({ section }) {
  const cols = section.columns || [];
  const rows = section.rows || [];
  const groups = section.groups || [];
  const grouped = Array.isArray(section.groups);
  const align = (a) => (a === 'right' ? 'text-right' : a === 'center' ? 'text-center' : 'text-left');
  const isEmpty = grouped ? groups.length === 0 : rows.length === 0;

  // Does this section render any fulfilled-% bars? (RM Issued only.) If so,
  // issued-but-no-requirement material cells get a reserved "no req." slot so
  // the material columns stay vertically aligned.
  const hasProgress = grouped
    ? groups.some((g) => (g.rows || []).some((r) => r && r._progress))
    : rows.some((r) => r && r._progress);

  const renderCells = (r, extra = '') =>
    cols.map((c) => {
      const raw = r[c.key];
      const value = (raw === undefined || raw === null || raw === '') ? '—' : raw;
      const baseTd = `px-3 py-2 align-top ${align(c.align)} ${extra}`;

      // --- Per-material fulfilled progress bar (RM Issued) ---
      const prog = r._progress && r._progress[c.key];
      if (prog) {
        const bar = BAR[prog.fill] || BAR.gold;
        const pct = Math.max(0, Number(prog.pct) || 0);
        const w = Math.min(100, pct);
        return (
          <td key={c.key} className={`px-3 py-2 align-top min-w-[140px] ${extra}`} data-testid="digest-fulfil-cell">
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-mono tabular-nums font-semibold text-[var(--ink-navy)] whitespace-nowrap">{value}</span>
              <span className="text-[11px] font-extrabold tabular-nums whitespace-nowrap" style={{ color: bar.text }} data-testid="digest-fulfil-pct">{pct}%</span>
            </div>
            <div className="mt-1.5 h-1.5 w-full rounded-full overflow-hidden" style={{ background: 'rgba(11,27,43,0.08)' }} title={`Requirement fulfilled: ${pct}%`}>
              <div className="h-full rounded-full" style={{ width: `${w}%`, background: `linear-gradient(90deg, ${bar.from}, ${bar.to})` }} />
            </div>
          </td>
        );
      }

      // --- Issued but NO requirement raised for this material ---------------
      // Keeps the material column aligned with bar-rows and signals why there
      // is no % (nothing to measure the issuance against).
      if (hasProgress && MATERIAL_KEYS.has(c.key) && value !== '—') {
        return (
          <td key={c.key} className={`px-3 py-2 align-top min-w-[140px] ${extra}`} data-testid="digest-fulfil-cell-noreq">
            <div className="flex items-baseline justify-between gap-2">
              <span className="font-mono tabular-nums font-semibold text-[var(--ink-navy)] whitespace-nowrap">{value}</span>
              <span className="text-[10px] font-medium tabular-nums whitespace-nowrap text-slate-400 italic" title="No requirement raised for this material — nothing to measure the issuance against">no req.</span>
            </div>
            {/* Invisible spacer matching the progress-bar height so numbers align. */}
            <div className="mt-1.5 h-1.5 w-full" aria-hidden="true" />
          </td>
        );
      }

      // --- Transfer From/To entity links (with Page No badge) ---
      const link = r._links && r._links[c.key];
      if (link) {
        if (link.kind === 'order' && link.oid) {
          return (
            <td key={c.key} className={`${baseTd} whitespace-nowrap font-medium`}>
              <a
                href={`/orders/${link.oid}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--diamond-icon)] hover:underline underline-offset-2 decoration-dotted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--diamond-icon)] rounded"
                data-testid="digest-transfer-order-link"
                title="Open this order form"
              >
                {value}
              </a>
              {link.page_badge && (
                <span className="ml-1.5 inline-flex items-center rounded-md bg-[var(--champagne)] px-1.5 py-0.5 text-[10px] font-semibold text-[var(--goldsmith-icon)] align-middle" data-testid="digest-transfer-page-badge">
                  {link.page_badge}
                </span>
              )}
            </td>
          );
        }
        // Unattributed pool — no link, muted style.
        return (
          <td key={c.key} className={`${baseTd} whitespace-nowrap text-[var(--goldsmith-icon)] italic`} data-testid="digest-transfer-pool">{value}</td>
        );
      }

      // --- Packet link (Purchases / Purchase Returns) ---
      if (c.key === 'ref' && r._packet && r._packet.id && PACKET_ROUTE[r._packet.kind]) {
        return (
          <td key={c.key} className={`${baseTd} whitespace-nowrap font-medium`}>
            <a
              href={`/${PACKET_ROUTE[r._packet.kind]}/packets/${r._packet.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--diamond-icon)] hover:underline underline-offset-2 decoration-dotted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--diamond-icon)] rounded"
              data-testid="digest-packet-link"
              title="Open this packet"
            >
              {value}
            </a>
          </td>
        );
      }

      // --- Order-form ref link (existing behaviour) ---
      if (c.key === 'ref' && r._oid) {
        return (
          <td key={c.key} className={`${baseTd} whitespace-nowrap font-medium text-[var(--ink-navy)]`}>
            <a
              href={`/orders/${r._oid}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--diamond-icon)] hover:underline underline-offset-2 decoration-dotted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--diamond-icon)] rounded"
              data-testid="digest-detail-orderform-link"
              title="Open this order form"
            >
              {value}
            </a>
          </td>
        );
      }

      // --- Plain cell ---
      return (
        <td key={c.key} className={`${baseTd} whitespace-nowrap ${c.align === 'right' ? 'font-mono tabular-nums' : ''} ${c.key === 'ref' ? 'font-medium text-[var(--ink-navy)]' : 'text-slate-700'}`}>
          {value}
        </td>
      );
    });

  return (
    <div data-testid={`digest-detail-section${section.title ? `-${section.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}` : ''}`}>
      {section.title && (
        <div className="text-[13px] font-bold uppercase tracking-[0.04em] text-[var(--ink-navy)] mb-2">{section.title}</div>
      )}
      {isEmpty ? (
        <div className="rounded-lg border border-dashed border-[var(--divider)] bg-[var(--paper-warm)] px-4 py-6 text-center text-xs text-slate-500">
          {section.empty || 'No records in this period.'}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[var(--divider)]">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="bg-[var(--diamond-bg)] text-[var(--diamond-icon)]">
                {cols.map((c) => (
                  <th key={c.key} className={`px-3 py-2 font-semibold text-[11px] uppercase tracking-wide whitespace-nowrap ${align(c.align)}`}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grouped
                ? groups.map((g, gi) => (
                    <React.Fragment key={gi}>
                      <tr className="border-t-2 border-[var(--gold-accent)] bg-[var(--champagne)]">
                        <td colSpan={cols.length} className="px-3 py-2 border-l-4 border-[var(--gold-accent)]">
                          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5">
                            {g.page_no !== null && g.page_no !== undefined && g.page_no !== '' ? (
                              <a
                                href={`/orders?reference_no=${encodeURIComponent(g.page_no)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[12px] font-bold uppercase tracking-[0.05em] text-[var(--ink-navy)] hover:text-[var(--diamond-icon)] hover:underline underline-offset-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--diamond-icon)] rounded"
                                data-testid="digest-detail-group-page"
                                title="Open all order forms on this page"
                              >
                                {g.title}
                              </a>
                            ) : (
                              <span className="text-[12px] font-bold uppercase tracking-[0.05em] text-[var(--ink-navy)]" data-testid="digest-detail-group-page">{g.title}</span>
                            )}
                            {g.customer && (
                              <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[var(--goldsmith-icon)]" data-testid="digest-detail-group-customer">
                                <span className="text-[var(--gold-accent)]">·</span>
                                <User size={12} className="text-[var(--gold-accent)]" /> {g.customer}
                              </span>
                            )}
                            {g.goldsmith && (
                              <span className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[var(--goldsmith-icon)]" data-testid="digest-detail-group-goldsmith">
                                <span className="text-[var(--gold-accent)]">·</span>
                                <Hammer size={12} className="text-[var(--gold-accent)]" /> {g.goldsmith}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                      {(g.rows || []).map((r, i) => (
                        <tr key={`${gi}-${i}`} className={i % 2 ? 'bg-[var(--paper-warm)]' : 'bg-white'}>
                          {renderCells(r)}
                        </tr>
                      ))}
                      {g.subtotal && (
                        <tr className="bg-[var(--diamond-bg)] text-[var(--ink-navy)] font-semibold">
                          {renderCells(g.subtotal, 'italic')}
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                : rows.map((r, i) => (
                    <tr key={i} className={i % 2 ? 'bg-[var(--paper-warm)]' : 'bg-white'}>
                      {renderCells(r)}
                    </tr>
                  ))}
              {section.total && (
                <tr className="bg-[var(--ink-navy)] text-white font-semibold">
                  {cols.map((c) => (
                    <td key={c.key} className={`px-3 py-2 whitespace-nowrap ${align(c.align)} ${c.align === 'right' ? 'font-mono tabular-nums' : ''}`}>
                      {section.total[c.key] ?? ''}
                    </td>
                  ))}
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default DigestDetailDialog;
